import React, { useId } from 'react'

import { useTheme } from '@/shared/contexts/ThemeContext'
import { YESFI_BRAND_MARK_COLORS, YesfiBrandMarkGraphic } from '@/shared/ui/YesfiBrandMark'
import { YESFI_MARK_CENTER, YESFI_MARK_VIEW_SIZE } from '@/shared/ui/yesfiBrandMarkPaths'

const FONT =
  "'Inter Variable', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Yu Gothic UI', sans-serif"

/** Outer-ring diameter in the logo coordinate system (cropped from the full ~190.9 unit mark viewBox). */
const LOGO_MARK_DIAMETER = 50

/** Compact viewBox — mark at (50, 50) + wordmark to the right, no tagline. */
const LOGO_VIEWBOX = { x: 25, y: 25, width: 185, height: 50 } as const

function logoViewBox() {
  return `${LOGO_VIEWBOX.x} ${LOGO_VIEWBOX.y} ${LOGO_VIEWBOX.width} ${LOGO_VIEWBOX.height}`
}

/**
 * Returns the appropriate brand mark colors for the current theme,
 * matching sayfi-frontend's DesktopBrandLogo behavior.
 */
function markColors(isDark: boolean) {
  return isDark ? YESFI_BRAND_MARK_COLORS.onBlue : YESFI_BRAND_MARK_COLORS.onBlueLight
}

/**
 * YesFi wordmark with the canonical brand mark (filled disc + rings + check).
 * Used in the global header — height 32px desktop, 26px mobile.
 *
 * 关联文档文件：YesfiBrandMark.tsx, yesfiBrandMarkPaths.ts, ThemeContext.tsx
 */
const YesFiWordmark: React.FC<{ height?: number }> = ({ height = 32 }) => {
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const clipId = useId().replace(/:/g, '')
  const colors = markColors(isDark)

  const markScale = LOGO_MARK_DIAMETER / YESFI_MARK_VIEW_SIZE
  const wordBase = 'var(--wordmark-fill)'
  const wordAccent = 'var(--brand-primary)'

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={logoViewBox()}
      preserveAspectRatio="xMinYMid meet"
      aria-hidden
      style={{
        display: 'block',
        height,
        width: 'auto',
        flexShrink: 0,
      }}
    >
      <defs>
        <clipPath id={clipId}>
          <rect width={80.3801} height={63.1558} fill="white" transform="translate(21.1983 33.2737)" />
        </clipPath>
      </defs>
      <g transform={`translate(50 50) scale(${markScale}) translate(${-YESFI_MARK_CENTER} ${-YESFI_MARK_CENTER})`}>
        <YesfiBrandMarkGraphic colors={colors} clipId={clipId} />
      </g>
      <text
        x={85}
        y={50}
        dominantBaseline="central"
        fontFamily={FONT}
        fontWeight={700}
        fontSize={21}
        letterSpacing="-0.85"
        fill={wordBase}
      >
        Yes
        <tspan dx={6} fill={wordAccent} letterSpacing="-0.85">
          F
        </tspan>
        <tspan dx={0} fill={wordAccent} letterSpacing="-0.6">
          i
        </tspan>
        <tspan dx={2} fontSize={10} fontWeight={500} letterSpacing="-0.3" fill={wordBase}>
          .com
        </tspan>
      </text>
    </svg>
  )
}

export default YesFiWordmark
